"use client"

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"

export type FrontendLocale = "fa" | "en"
export type FrontendTheme = "light" | "dark"

const persianFieldLabels: Record<string, string> = {
  "First name": "نام",
  "Last name": "نام خانوادگی",
  "Full name": "نام و نام خانوادگی",
  Email: "ایمیل",
  Password: "رمز عبور",
  "Old password": "رمز عبور فعلی",
  "New password": "رمز عبور جدید",
  "Confirm password": "تأیید رمز عبور",
  Phone: "شماره تماس",
  Company: "شرکت",
  Address: "نشانی",
  "Address line 1": "نشانی",
  "Address line 2": "واحد، پلاک یا توضیحات تکمیلی",
  "Apartment, suite, etc.": "واحد، پلاک یا توضیحات تکمیلی",
  "Postal code": "کد پستی",
  City: "شهر",
  Province: "استان",
  "State / Province": "استان",
  Country: "کشور",
  "Order ID": "شناسه سفارش",
  Name: "نام",
  "Billing address": "نشانی صورتحساب",
  "Shipping address": "نشانی ارسال",
  "No billing address": "نشانی صورتحسابی ثبت نشده است",
  "Edit address": "ویرایش نشانی",
  "Add address": "افزودن نشانی",
  "New address": "نشانی جدید",
  "Shipping Addresses": "نشانی‌های ارسال",
  Edit: "ویرایش",
  Remove: "حذف",
  Cancel: "انصراف",
  Save: "ذخیره",
  "Save changes": "ذخیره تغییرات",
  "An error occurred, please try again": "خطایی رخ داد؛ دوباره تلاش کنید.",
  "Email verification": "تأیید ایمیل",
  "Verifying your email...": "در حال تأیید ایمیل شما…",
  "Your email is verified. You can now sign in to your account.": "ایمیل شما تأیید شد. اکنون می‌توانید وارد حساب کاربری خود شوید.",
  "This verification link is invalid or has expired. Sign in to receive a new verification email.": "این لینک تأیید نامعتبر است یا اعتبار آن پایان یافته است. وارد حساب شوید تا یک ایمیل تأیید جدید دریافت کنید.",
  "Go to sign in": "ورود به حساب کاربری",
  item: "کالا",
  items: "کالا",
  more: "بیشتر",
  "See details": "مشاهده جزئیات",
  "The password is not shown for security reasons": "برای حفظ امنیت، رمز عبور نمایش داده نمی‌شود.",
}

export const localizeFrontendFieldLabel = (
  label: string,
  locale: FrontendLocale
) => (locale === "fa" ? persianFieldLabels[label] ?? label : label)

type HeroCopy = {
  eyebrow: string
  title: string
  body: string
  cta: string
}

type FrontendCopy = {
  nav: {
    menu: string
    scarves: string
    categories: string
    collections: string
    account: string
    cart: string
  }
  cart: {
    title: string
    item: string
    quantity: string
    price: string
    total: string
    summary: string
    signInTitle: string
    signInBody: string
    signIn: string
    promotionCodes: string
    apply: string
    promotionsApplied: string
    removePromotionCode: string
    remove: string
    subtotal: string
    shipping: string
    discount: string
    taxes: string
    checkout: string
    goToCart: string
    variant: string
    emptyTitle: string
    emptyBody: string
    exploreProducts: string
  }
  menu: {
    house: string
    primary: Array<{ name: string; href: string }>
    customerCare: string
    shoppingBag: string
    discover: string
    ourWorld: string
    allScarves: string
    preferences: string
    country: string
  }
  hero: {
    ariaLabel: string
    chooseSlide: string
    dragHint: string
    slides: HeroCopy[]
  }
  home: {
    editorialAria: string
    pairedEditorialAria: string
    scarves: string
    resortCollection: string
    tiles: string[]
    previous: string
    next: string
    newsletterTag: string
    newsletterLine: string
    newsletterEmphasis: string
    newsletterCta: string
    aboutTag: string
    aboutText: string
  }
  bestSellers: {
    eyebrow: string
    title: string
    hint: string
  }
  catalog: {
    sortBy: string
    results: string
    filters: string
    latest: string
    priceAsc: string
    priceDesc: string
    eyebrow: string
    title: string
    body: string
    options: string
    option: string
    size: string
    color: string
    white: string
    black: string
  }
  categoryIndex: {
    eyebrow: string
    title: string
    body: string
    empty: string
    view: string
    products: string
    noMedia: string
    home: string
    subcategories: string
  }
  collectionIndex: {
    eyebrow: string
    title: string
    body: string
    empty: string
    view: string
  }
  footer: {
    intro: string
    categoryHeading: string
    collectionsHeading: string
    shoreHeading: string
    allScarves: string
    account: string
    shoppingBag: string
    madeFor: string
  }
  controls: {
    switchToPersian: string
    switchToEnglish: string
    switchToLight: string
    switchToDark: string
  }
  account: {
    signInTitle: string
    signInBody: string
    verificationEmailSent: string
    verificationSignInPrompt: string
    email: string
    password: string
    emailValidation: string
    signIn: string
    notMember: string
    joinUs: string
    registerTitle: string
    registerBody: string
    verificationCheckInbox: string
    firstName: string
    lastName: string
    phone: string
    consentPrefix: string
    privacyPolicy: string
    consentAnd: string
    termsOfUse: string
    join: string
    alreadyMember: string
    supportTitle: string
    supportBody: string
    customerService: string
    dashboard: string
    overview: string
    profile: string
    addresses: string
    orders: string
    logOut: string
    greeting: string
    signedInAs: string
    completed: string
    saved: string
    recentOrders: string
    datePlaced: string
    orderNumber: string
    totalAmount: string
    noRecentOrders: string
    goToOrder: string
    noOrdersTitle: string
    noOrdersBody: string
    continueShopping: string
    pages: {
      profile: { title: string; body: string }
      addresses: { title: string; body: string }
      orders: { title: string; body: string }
    }
  }
  checkout: {
    savedAddressPrompt: string
    sameAsBilling: string
    emailValidation: string
    shippingAddress: string
    billingAddress: string
    contact: string
    edit: string
    continueToDelivery: string
    billingMatchesShipping: string
    delivery: string
    shippingMethod: string
    deliveryQuestion: string
    pickupOrder: string
    store: string
    chooseStore: string
    continueToPayment: string
    method: string
    payment: string
    paymentMethod: string
    paymentDetails: string
    giftCard: string
    enterCardDetails: string
    continueToReview: string
    anotherStep: string
    review: string
    orderConsent: string
    placeOrder: string
    selectPaymentMethod: string
    backToCart: string
    back: string
    secureCheckout: string
    orderSummary: string
    stepAddress: string
    stepDone: string
    deliveryHint: string
    reviewHint: string
  }
  order: {
    thankYou: string
    placed: string
    confirmationSent: string
    orderDate: string
    orderNumber: string
    orderStatus: string
    paymentStatus: string
    summary: string
    items: string
    delivery: string
    shippingAddress: string
    contact: string
    method: string
    payment: string
    paymentMethod: string
    paymentDetails: string
    paidAt: string
    needHelp: string
    contactUs: string
    returns: string
    orderDetails: string
    backToOverview: string
    total: string
    email: string
    continueShopping: string
  }
  product: {
    select: string
    selectOptions: string
    selectVariant: string
    outOfStock: string
    addToCart: string
    from: string
    original: string
    productInformation: string
    shippingAndReturns: string
    material: string
    countryOfOrigin: string
    type: string
    weight: string
    dimensions: string
    grams: string
    fastDelivery: string
    fastDeliveryBody: string
    simpleExchanges: string
    simpleExchangesBody: string
    easyReturns: string
    easyReturnsBody: string
    relatedProducts: string
    relatedProductsBody: string
  }
}

const copy: Record<
  FrontendLocale,
  Omit<
    FrontendCopy,
    "home" | "bestSellers" | "catalog" | "collectionIndex" | "categoryIndex" | "footer"
  >
> = {
  fa: {
    nav: {
      menu: "فهرست",
      scarves: "روسری‌ها",
      categories: "دسته‌بندی‌ها",
      collections: "مجموعه‌ها",
      account: "حساب کاربری",
      cart: "سبد خرید",
    },
    cart: {
      title: "سبد خرید",
      item: "کالا",
      quantity: "تعداد",
      price: "قیمت",
      total: "مجموع",
      summary: "خلاصه سفارش",
      signInTitle: "حساب کاربری دارید؟",
      signInBody: "برای تجربه‌ای بهتر وارد حساب خود شوید.",
      signIn: "ورود",
      promotionCodes: "افزودن کد تخفیف",
      apply: "اعمال",
      promotionsApplied: "کدهای تخفیف اعمال‌شده:",
      removePromotionCode: "حذف کد تخفیف از سفارش",
      remove: "حذف",
      subtotal: "جمع کالاها",
      shipping: "هزینه ارسال",
      discount: "تخفیف",
      taxes: "مالیات",
      checkout: "ادامه برای پرداخت",
      goToCart: "مشاهده سبد خرید",
      variant: "مدل",
      emptyTitle: "سبد خرید شما خالی است",
      emptyBody: "برای انتخاب یک روسری یا شال تازه، مجموعه‌ها را ببینید.",
      exploreProducts: "مشاهده محصولات",
    },
    menu: {
      house: "ساحل / خانه ساحلی",
      primary: [
        { name: "تازه‌ها", href: "/store" },
        { name: "فروشگاه", href: "/store" },
        { name: "دسته‌بندی‌ها", href: "/categories" },
        { name: "مجموعه‌ها", href: "/collections" },
        { name: "حراج", href: "/store" },
      ],
      customerCare: "راهنمای مشتریان",
      shoppingBag: "سبد خرید",
      discover: "دنیای ساحل",
      ourWorld: "دنیای ما",
      allScarves: "همه روسری‌ها",
      preferences: "زبان و نمایش",
      country: "کشور / منطقه",
    },
    hero: {
      ariaLabel: "گزیده‌های ساحلی ساحل",
      chooseSlide: "انتخاب اسلاید",
      dragHint: "برای دیدن ادامه، بکشید",
      slides: [
        {
          eyebrow: "حرکت ساحل",
          title: "ابریشم در آغوش موج",
          body: "مطالعه‌ای آرام از آب، نور و حرکت آزاد ابریشم.",
          cta: "مشاهده روسری‌ها",
        },
        {
          eyebrow: "ماسه مرواریدی",
          title: "بیانی نرم‌تر از لطافت",
          body: "ابریشم چاپی در حال‌وهوای صبحی آرام کنار ساحل.",
          cta: "کشف مجموعه",
        },
        {
          eyebrow: "نور صدف",
          title: "بگذارید روز آرام بگذرد",
          body: "رنگ، آب و روسری‌ای برای افق‌های باز.",
          cta: "کاوش روسری‌ها",
        },
        {
          eyebrow: "شماره یک",
          title: "رنگی آرام، حضوری کامل",
          body: "مروارید و لاگون عمیق در یک تاخوردگی روان.",
          cta: "خرید شماره یک",
        },
        {
          eyebrow: "بته‌جقه نرم",
          title: "میراثی در رنگ دریا",
          body: "قوس‌های اصیل در ماسه نمکین، فیروزه و صدف صورتی.",
          cta: "کشف بته‌جقه",
        },
        {
          eyebrow: "خال‌خالی",
          title: "صدفی کوچک و صورتی",
          body: "روسری ابریشمی گره‌خورده برای روزهایی که کنار آب آغاز می‌شوند.",
          cta: "کاوش خال‌خالی",
        },
        {
          eyebrow: "حرکت ساحل",
          title: "جریان، مروارید و ابریشم",
          body: "پژوهشی دیگر در حرکت ابریشم، بدون مدل و تصویر بدن.",
          cta: "عبور از مجموعه",
        },
        {
          eyebrow: "امضای ساحل",
          title: "نشان خط ساحلی",
          body: "مروارید، صدف و آرامش آب در کامل‌ترین شکل خود.",
          cta: "کشف دنیای ما",
        },
      ],
    },
    controls: {
      switchToPersian: "تغییر زبان به فارسی",
      switchToEnglish: "Change language to English",
      switchToLight: "فعال‌کردن نمایش روشن",
      switchToDark: "فعال‌کردن نمایش تیره",
    },
    account: {
      signInTitle: "خوش آمدید",
      signInBody: "برای دسترسی به حساب کاربری و تجربه‌ای شخصی‌تر وارد شوید.",
      verificationEmailSent: "پیوند تأیید به",
      verificationSignInPrompt: "ارسال شد. ابتدا ایمیل خود را تأیید کنید، سپس وارد شوید.",
      email: "ایمیل",
      password: "رمز عبور",
      emailValidation: "یک نشانی ایمیل معتبر وارد کنید.",
      signIn: "ورود",
      notMember: "حساب کاربری ندارید؟",
      joinUs: "عضویت",
      registerTitle: "به جمع ساحل بپیوندید",
      registerBody: "پروفایل خود را بسازید و تجربه‌ای شخصی‌تر از خرید داشته باشید.",
      verificationCheckInbox: "ارسال شد. صندوق ورودی را بررسی کنید، ایمیل را تأیید کنید و سپس وارد شوید.",
      firstName: "نام",
      lastName: "نام خانوادگی",
      phone: "شماره تماس",
      consentPrefix: "با ساخت حساب، با",
      privacyPolicy: "حریم خصوصی",
      consentAnd: "و",
      termsOfUse: "شرایط استفاده",
      join: "عضویت",
      alreadyMember: "از پیش عضو هستید؟",
      supportTitle: "پرسشی دارید؟",
      supportBody: "پاسخ پرسش‌های متداول را در صفحهٔ راهنمای مشتریان پیدا کنید.",
      customerService: "راهنمای مشتریان",
      dashboard: "حساب کاربری",
      overview: "نمای کلی",
      profile: "پروفایل",
      addresses: "نشانی‌ها",
      orders: "سفارش‌ها",
      logOut: "خروج",
      greeting: "سلام {name}",
      signedInAs: "واردشده با:",
      completed: "تکمیل‌شده",
      saved: "ذخیره‌شده",
      recentOrders: "سفارش‌های اخیر",
      datePlaced: "تاریخ ثبت",
      orderNumber: "شماره سفارش",
      totalAmount: "مبلغ کل",
      noRecentOrders: "هنوز سفارشی ثبت نشده است",
      goToOrder: "مشاهده سفارش شماره {id}",
      noOrdersTitle: "هنوز سفارشی برای نمایش نیست",
      noOrdersBody: "هنوز سفارشی ثبت نکرده‌اید؛ بیایید اولین انتخابتان را پیدا کنیم.",
      continueShopping: "ادامه خرید",
      pages: {
        profile: {
          title: "پروفایل",
          body: "اطلاعات پروفایل خود از جمله نام، ایمیل و شماره تماس را مشاهده و ویرایش کنید. همچنین می‌توانید نشانی صورتحساب یا رمز عبور خود را به‌روزرسانی کنید.",
        },
        addresses: {
          title: "نشانی‌های ارسال",
          body: "نشانی‌های ذخیره‌شدهٔ خود را برای خریدهای سریع‌تر مدیریت کنید.",
        },
        orders: {
          title: "سفارش‌ها",
          body: "سفارش‌های ثبت‌شده و جزئیات آن‌ها را اینجا مشاهده کنید.",
        },
      },
    },
    checkout: {
      savedAddressPrompt: "سلام {name}، مایلید یکی از نشانی‌های ذخیره‌شده را استفاده کنید؟",
      sameAsBilling: "نشانی صورتحساب با نشانی ارسال یکسان است",
      emailValidation: "یک نشانی ایمیل معتبر وارد کنید.",
      shippingAddress: "نشانی ارسال",
      billingAddress: "نشانی صورتحساب",
      contact: "اطلاعات تماس",
      edit: "ویرایش",
      continueToDelivery: "ادامه برای انتخاب ارسال",
      billingMatchesShipping: "نشانی صورتحساب و ارسال یکسان است.",
      delivery: "ارسال",
      shippingMethod: "روش ارسال",
      deliveryQuestion: "سفارش خود را چگونه دریافت می‌کنید؟",
      pickupOrder: "تحویل حضوری سفارش",
      store: "فروشگاه",
      chooseStore: "فروشگاه نزدیک خود را انتخاب کنید",
      continueToPayment: "ادامه برای پرداخت",
      method: "روش",
      payment: "پرداخت",
      paymentMethod: "روش پرداخت",
      paymentDetails: "جزئیات پرداخت",
      giftCard: "کارت هدیه",
      enterCardDetails: "ورود اطلاعات کارت",
      continueToReview: "ادامه برای بازبینی سفارش",
      anotherStep: "مرحلهٔ دیگری نمایش داده می‌شود",
      review: "بازبینی سفارش",
      orderConsent: "با انتخاب ثبت سفارش، تأیید می‌کنید که شرایط استفاده، شرایط فروش و سیاست بازگشت را مطالعه و پذیرفته‌اید و از سیاست حریم خصوصی ساحل آگاه هستید.",
      placeOrder: "ثبت سفارش",
      selectPaymentMethod: "یک روش پرداخت انتخاب کنید",
      backToCart: "بازگشت به سبد خرید",
      back: "مرحلهٔ قبل",
      secureCheckout: "پرداخت امن",
      orderSummary: "خلاصه سفارش",
      stepAddress: "نشانی",
      stepDone: "انجام شد",
      deliveryHint: "روش دریافت سفارش را انتخاب کنید.",
      reviewHint: "همه‌چیز را یک بار بررسی کنید، سپس سفارش را ثبت کنید.",
    },
    order: {
      thankYou: "سپاس از خرید شما!",
      placed: "سفارش شما با موفقیت ثبت شد.",
      confirmationSent: "جزئیات تأیید سفارش به {email} ارسال شد.",
      orderDate: "تاریخ سفارش",
      orderNumber: "شماره سفارش",
      orderStatus: "وضعیت سفارش",
      paymentStatus: "وضعیت پرداخت",
      summary: "خلاصه سفارش",
      items: "کالاها",
      delivery: "ارسال",
      shippingAddress: "نشانی ارسال",
      contact: "اطلاعات تماس",
      method: "روش ارسال",
      payment: "پرداخت",
      paymentMethod: "روش پرداخت",
      paymentDetails: "جزئیات پرداخت",
      paidAt: "پرداخت‌شده در",
      needHelp: "کمک می‌خواهید؟",
      contactUs: "تماس با ما",
      returns: "بازگشت و تعویض کالا",
      orderDetails: "جزئیات سفارش",
      backToOverview: "بازگشت به سفارش‌ها",
      total: "مجموع",
      email: "ایمیل",
      continueShopping: "ادامه خرید",
    },
    product: {
      select: "انتخاب",
      selectOptions: "انتخاب گزینه‌ها",
      selectVariant: "انتخاب مدل",
      outOfStock: "ناموجود",
      addToCart: "افزودن به سبد خرید",
      from: "از",
      original: "قیمت اصلی:",
      productInformation: "اطلاعات محصول",
      shippingAndReturns: "ارسال و بازگشت",
      material: "جنس",
      countryOfOrigin: "کشور سازنده",
      type: "نوع",
      weight: "وزن",
      dimensions: "ابعاد",
      grams: "گرم",
      fastDelivery: "ارسال سریع",
      fastDeliveryBody: "سفارش شما طی ۳ تا ۵ روز کاری به نشانی انتخابی‌تان ارسال می‌شود.",
      simpleExchanges: "تعویض آسان",
      simpleExchangesBody: "اگر انتخابتان مناسب نبود، برای تعویض محصول کنارتان هستیم.",
      easyReturns: "بازگشت آسان",
      easyReturnsBody: "کالای خود را بازگردانید تا مبلغ آن طبق شرایط بازگشت به شما بازگردانده شود.",
      relatedProducts: "محصولات مرتبط",
      relatedProductsBody: "شاید این انتخاب‌ها را هم دوست داشته باشید.",
    },
  },
  en: {
    nav: {
      menu: "Menu",
      scarves: "Scarves",
      categories: "Categories",
      collections: "Collections",
      account: "Account",
      cart: "Cart",
    },
    cart: {
      title: "Cart",
      item: "Item",
      quantity: "Quantity",
      price: "Price",
      total: "Total",
      summary: "Summary",
      signInTitle: "Already have an account?",
      signInBody: "Sign in for a better experience.",
      signIn: "Sign in",
      promotionCodes: "Add promotion code(s)",
      apply: "Apply",
      promotionsApplied: "Promotion(s) applied:",
      removePromotionCode: "Remove discount code from order",
      remove: "Remove",
      subtotal: "Subtotal (excl. shipping and taxes)",
      shipping: "Shipping",
      discount: "Discount",
      taxes: "Taxes",
      checkout: "Go to checkout",
      goToCart: "Go to cart",
      variant: "Variant",
      emptyTitle: "Your cart is empty",
      emptyBody: "Explore our collection to find a piece you love.",
      exploreProducts: "Explore products",
    },
    menu: {
      house: "SAHIL / SHORELINE HOUSE",
      primary: [
        { name: "New", href: "/store" },
        { name: "Shop", href: "/store" },
        { name: "Categories", href: "/categories" },
        { name: "Collections", href: "/collections" },
        { name: "Sale", href: "/store" },
      ],
      customerCare: "Customer care",
      shoppingBag: "Shopping bag",
      discover: "Discover Sahil",
      ourWorld: "Our world",
      allScarves: "All scarves",
      preferences: "Language & display",
      country: "Country / region",
    },
    hero: {
      ariaLabel: "Sahil editorial highlights",
      chooseSlide: "Choose a hero slide",
      dragHint: "Drag to explore",
      slides: [
        {
          eyebrow: "Sahil motion",
          title: "Silk, caught in the tide",
          body: "A scarf-only study in water, light, and unhurried movement.",
          cta: "Shop scarves",
        },
        {
          eyebrow: "Pearl sand",
          title: "A softer kind of statement",
          body: "Printed silk arranged like a quiet morning shoreline.",
          cta: "Discover the edit",
        },
        {
          eyebrow: "Shell light",
          title: "Let the day move slowly",
          body: "Color, water, and a scarf made for open horizons.",
          cta: "Explore scarves",
        },
        {
          eyebrow: "No. 1",
          title: "Quiet color, full relief",
          body: "Pearl and deep lagoon held in a single flowing fold.",
          cta: "Shop No. 1",
        },
        {
          eyebrow: "Paisley, softened",
          title: "An heirloom in sea glass",
          body: "Heritage curves recast in salt sand, turquoise, and rose shell.",
          cta: "Discover Paisley",
        },
        {
          eyebrow: "Polkadot",
          title: "A little rose shell",
          body: "A knotted silk scarf for days that begin beside the water.",
          cta: "Explore Polkadot",
        },
        {
          eyebrow: "Sahil motion",
          title: "Current, pearl, and silk",
          body: "A second scarf-only motion study, with no model and no body imagery.",
          cta: "Move through the collection",
        },
        {
          eyebrow: "Sahil signature",
          title: "The mark of the shoreline",
          body: "A pearl, a shell, and the calm of water held in full relief.",
          cta: "Discover our world",
        },
      ],
    },
    controls: {
      switchToPersian: "تغییر زبان به فارسی",
      switchToEnglish: "Change language to English",
      switchToLight: "Use light appearance",
      switchToDark: "Use dark appearance",
    },
    account: {
      signInTitle: "Welcome back",
      signInBody: "Sign in to access a more personal shopping experience.",
      verificationEmailSent: "We sent a verification link to",
      verificationSignInPrompt: "Please verify your email, then sign in.",
      email: "Email",
      password: "Password",
      emailValidation: "Enter a valid email address.",
      signIn: "Sign in",
      notMember: "Not a member?",
      joinUs: "Join us",
      registerTitle: "Join Sahil",
      registerBody: "Create your profile for a more personal shopping experience.",
      verificationCheckInbox: "Please check your inbox to verify your email, then sign in.",
      firstName: "First name",
      lastName: "Last name",
      phone: "Phone",
      consentPrefix: "By creating an account, you agree to Sahil’s",
      privacyPolicy: "Privacy Policy",
      consentAnd: "and",
      termsOfUse: "Terms of Use",
      join: "Join",
      alreadyMember: "Already a member?",
      supportTitle: "Got questions?",
      supportBody: "You can find frequently asked questions and answers on our customer service page.",
      customerService: "Customer Service",
      dashboard: "Account",
      overview: "Overview",
      profile: "Profile",
      addresses: "Addresses",
      orders: "Orders",
      logOut: "Log out",
      greeting: "Hello {name}",
      signedInAs: "Signed in as:",
      completed: "Completed",
      saved: "Saved",
      recentOrders: "Recent orders",
      datePlaced: "Date placed",
      orderNumber: "Order number",
      totalAmount: "Total amount",
      noRecentOrders: "No recent orders",
      goToOrder: "Go to order #{id}",
      noOrdersTitle: "Nothing to see here",
      noOrdersBody: "You don't have any orders yet, let us change that :)",
      continueShopping: "Continue shopping",
      pages: {
        profile: {
          title: "Profile",
          body: "View and update your profile information, including your name, email, and phone number. You can also update your billing address or change your password.",
        },
        addresses: {
          title: "Shipping addresses",
          body: "Manage your saved addresses for faster checkout.",
        },
        orders: {
          title: "Orders",
          body: "Review your placed orders and their details here.",
        },
      },
    },
    checkout: {
      savedAddressPrompt: "Hi {name}, do you want to use one of your saved addresses?",
      sameAsBilling: "Billing address is the same as the shipping address",
      emailValidation: "Enter a valid email address.",
      shippingAddress: "Shipping address",
      billingAddress: "Billing address",
      contact: "Contact",
      edit: "Edit",
      continueToDelivery: "Continue to delivery",
      billingMatchesShipping: "Billing and delivery address are the same.",
      delivery: "Delivery",
      shippingMethod: "Shipping method",
      deliveryQuestion: "How would you like your order delivered?",
      pickupOrder: "Pick up your order",
      store: "Store",
      chooseStore: "Choose a store near you",
      continueToPayment: "Continue to payment",
      method: "Method",
      payment: "Payment",
      paymentMethod: "Payment method",
      paymentDetails: "Payment details",
      giftCard: "Gift card",
      enterCardDetails: "Enter card details",
      continueToReview: "Continue to review",
      anotherStep: "Another step will appear",
      review: "Review",
      orderConsent: "By clicking the Place Order button, you confirm that you have read, understand and accept our Terms of Use, Terms of Sale and Returns Policy and acknowledge that you have read Sahil’s Privacy Policy.",
      placeOrder: "Place order",
      selectPaymentMethod: "Select a payment method",
      backToCart: "Back to cart",
      back: "Previous step",
      secureCheckout: "Secure checkout",
      orderSummary: "Order summary",
      stepAddress: "Address",
      stepDone: "Done",
      deliveryHint: "Choose how you would like to receive your order.",
      reviewHint: "Take one last look, then place your order.",
    },
    order: {
      thankYou: "Thank you!",
      placed: "Your order was placed successfully.",
      confirmationSent: "We have sent the order confirmation details to {email}.",
      orderDate: "Order date",
      orderNumber: "Order number",
      orderStatus: "Order status",
      paymentStatus: "Payment status",
      summary: "Order summary",
      items: "Items",
      delivery: "Delivery",
      shippingAddress: "Shipping address",
      contact: "Contact",
      method: "Method",
      payment: "Payment",
      paymentMethod: "Payment method",
      paymentDetails: "Payment details",
      paidAt: "paid at",
      needHelp: "Need help?",
      contactUs: "Contact us",
      returns: "Returns & exchanges",
      orderDetails: "Order details",
      backToOverview: "Back to overview",
      total: "Total",
      email: "Email",
      continueShopping: "Continue shopping",
    },
    product: {
      select: "Select",
      selectOptions: "Select options",
      selectVariant: "Select variant",
      outOfStock: "Out of stock",
      addToCart: "Add to cart",
      from: "From",
      original: "Original:",
      productInformation: "Product information",
      shippingAndReturns: "Shipping & returns",
      material: "Material",
      countryOfOrigin: "Country of origin",
      type: "Type",
      weight: "Weight",
      dimensions: "Dimensions",
      grams: "g",
      fastDelivery: "Fast delivery",
      fastDeliveryBody: "Your package will arrive in 3–5 business days at your chosen address.",
      simpleExchanges: "Simple exchanges",
      simpleExchangesBody: "If your selection is not quite right, we are here to help with an exchange.",
      easyReturns: "Easy returns",
      easyReturnsBody: "Return your item and we will refund it in line with our returns policy.",
      relatedProducts: "Related products",
      relatedProductsBody: "You might also want to explore these pieces.",
    },
  },
}

const homeCopy: Pick<
  FrontendCopy,
  "home" | "bestSellers" | "catalog" | "collectionIndex" | "categoryIndex" | "footer"
> extends infer T
  ? Record<FrontendLocale, T>
  : never = {
  fa: {
    home: {
      editorialAria: "تصویرسازی روسری ساحل",
      pairedEditorialAria: "تصویرسازی دوتایی مجموعه ساحلی ساحل",
      scarves: "روسری‌ها",
      resortCollection: "مجموعه ساحلی",
      tiles: [
        "روسری شماره یک",
        "روسری راه‌راه",
        "روسری بته‌جقه",
        "روسری پنبه‌ای",
        "روسری ابریشمی",
      ],
      previous: "نمایش آیتم قبلی",
      next: "نمایش آیتم بعدی",
      newsletterTag: "به دنیای ما بپیوندید",
      newsletterLine: "برای دسترسی زودهنگام و",
      newsletterEmphasis: "معرفی‌های اختصاصی.",
      newsletterCta: "عضویت",
      aboutTag: "درباره ما",
      aboutText:
        "روسری‌ها و پوشاکی ماندگار برای استایلی بی‌دردسر؛ ساخته‌شده برای پوشیدن در فصل‌های پی‌درپی.",
    },
    bestSellers: {
      eyebrow: "پرفروش‌ترین‌ها",
      title: "محبوب‌ترین قطعات ما",
      hint: "برای کاوش بکشید",
    },
    catalog: {
      sortBy: "مرتب‌سازی",
      results: "{count} محصول",
      filters: "فیلترها",
      latest: "جدیدترین‌ها",
      priceAsc: "قیمت: کم به زیاد",
      priceDesc: "قیمت: زیاد به کم",
      eyebrow: "خانهٔ روسری / مجموعهٔ کامل",
      title: "همهٔ محصولات",
      body: "ابریشم، نقش و رنگ ساحلی برای هر فصل.",
      options: "گزینه‌ها",
      option: "گزینه",
      size: "سایز",
      color: "رنگ",
      white: "سفید",
      black: "مشکی",
    },
    categoryIndex: {
      eyebrow: "فروشگاه ساحل",
      title: "دسته‌بندی‌ها",
      body: "محصولات را بر اساس دسته‌بندی ببینید و سریع‌تر به چیزی که می‌خواهید برسید.",
      empty: "هنوز دسته‌بندی‌ای برای نمایش وجود ندارد.",
      view: "مشاهده محصولات",
      products: "{count} محصول",
      noMedia: "بدون تصویر",
      home: "خانه",
      subcategories: "زیر‌دسته‌ها",
    },
    collectionIndex: {
      eyebrow: "ساحل / مجموعه‌ها",
      title: "مجموعه‌ها",
      body: "مجموعه‌های منتخب ساحل را برای یافتن داستان و حال‌وهوای مورد علاقه‌تان ببینید.",
      empty: "هنوز مجموعه‌ای برای نمایش وجود ندارد.",
      view: "مشاهده مجموعه",
    },
    footer: {
      intro: "روسری‌ها و شال‌هایی برای بیانی نرم‌تر و شخصی‌تر.",
      categoryHeading: "خرید براساس دسته‌بندی",
      collectionsHeading: "مجموعه‌ها",
      shoreHeading: "از ساحل",
      allScarves: "همه روسری‌ها",
      account: "حساب کاربری من",
      shoppingBag: "سبد خرید",
      madeFor: "برای روزهای بلند و آفتابی ساخته شده",
    },
  },
  en: {
    home: {
      editorialAria: "Sahil scarf editorial",
      pairedEditorialAria: "Sahil paired coastal collection editorial",
      scarves: "Scarves",
      resortCollection: "Resort collection",
      tiles: [
        "No. 1 scarf",
        "Striped scarf",
        "Paisley scarf",
        "Cotton scarf",
        "Silk scarf",
      ],
      previous: "Show previous item",
      next: "Show next item",
      newsletterTag: "Join our world",
      newsletterLine: "For early access and",
      newsletterEmphasis: "private drops.",
      newsletterCta: "Join",
      aboutTag: "About us",
      aboutText:
        "Timeless scarves and ready-to-wear for effortless dressing. Made to be worn season after season.",
    },
    bestSellers: {
      eyebrow: "Best sellers",
      title: "Our most loved pieces",
      hint: "Swipe to discover",
    },
    catalog: {
      sortBy: "Sort by",
      results: "{count} products",
      filters: "Filters",
      latest: "Latest arrivals",
      priceAsc: "Price: low to high",
      priceDesc: "Price: high to low",
      eyebrow: "Scarf house / the complete collection",
      title: "All products",
      body: "Silk, pattern, and shoreline color for every season.",
      options: "Options",
      option: "Option",
      size: "Size",
      color: "Color",
      white: "White",
      black: "Black",
    },
    categoryIndex: {
      eyebrow: "Sahil store",
      title: "Categories",
      body: "Browse our products by category and get to what you need faster.",
      empty: "No categories are available yet.",
      view: "Shop now",
      products: "{count} products",
      noMedia: "No image yet",
      home: "Home",
      subcategories: "Subcategories",
    },
    collectionIndex: {
      eyebrow: "Sahil / Collections",
      title: "Collections",
      body: "Explore Sahil’s selected collections and find the story that feels like yours.",
      empty: "No collections are available yet.",
      view: "View collection",
    },
    footer: {
      intro: "Scarves and shawls for a softer kind of statement.",
      categoryHeading: "Shop by category",
      collectionsHeading: "Collections",
      shoreHeading: "From the shore",
      allScarves: "All scarves",
      account: "My account",
      shoppingBag: "Shopping bag",
      madeFor: "Made for long, sunlit days",
    },
  },
}

type FrontendPreferencesContextValue = {
  locale: FrontendLocale
  theme: FrontendTheme
  isHydrated: boolean
  t: FrontendCopy
  setLocale: (locale: FrontendLocale) => void
  toggleTheme: () => void
}

const FrontendPreferencesContext =
  createContext<FrontendPreferencesContextValue | null>(null)

const applyDocumentPreferences = (
  locale: FrontendLocale,
  theme: FrontendTheme
) => {
  const html = document.documentElement
  html.lang = locale
  html.dir = locale === "fa" ? "rtl" : "ltr"
  html.dataset.theme = theme
}

const PREFERENCE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const writePreferenceCookie = (name: string, value: string) => {
  document.cookie = `${name}=${value}; path=/; max-age=${PREFERENCE_COOKIE_MAX_AGE}; samesite=lax`
}

export const FrontendPreferencesProvider = ({
  children,
  initialLocale = "fa",
  initialTheme = "light",
}: {
  children: ReactNode
  /** Read from cookies on the server so SSR and the first client render agree. */
  initialLocale?: FrontendLocale
  initialTheme?: FrontendTheme
}) => {
  const [locale, setLocaleState] = useState<FrontendLocale>(initialLocale)
  const [theme, setTheme] = useState<FrontendTheme>(initialTheme)
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    const savedLocale = window.localStorage.getItem("sahil:locale")
    const savedTheme = window.localStorage.getItem("sahil:theme")
    const nextLocale: FrontendLocale = savedLocale === "en" ? "en" : "fa"
    const nextTheme: FrontendTheme = savedTheme === "dark" ? "dark" : "light"

    // Migrate preferences saved before cookies existed.
    writePreferenceCookie("sahil_locale", nextLocale)
    writePreferenceCookie("sahil_theme", nextTheme)
    setLocaleState(nextLocale)
    setTheme(nextTheme)
    applyDocumentPreferences(nextLocale, nextTheme)
    setIsHydrated(true)
  }, [])

  const setLocale = (nextLocale: FrontendLocale) => {
    setLocaleState(nextLocale)
    window.localStorage.setItem("sahil:locale", nextLocale)
    writePreferenceCookie("sahil_locale", nextLocale)
    applyDocumentPreferences(nextLocale, theme)
  }

  const toggleTheme = () => {
    const nextTheme: FrontendTheme = theme === "light" ? "dark" : "light"
    setTheme(nextTheme)
    window.localStorage.setItem("sahil:theme", nextTheme)
    writePreferenceCookie("sahil_theme", nextTheme)
    applyDocumentPreferences(locale, nextTheme)
  }

  return (
    <FrontendPreferencesContext.Provider
      value={{
        locale,
        theme,
        isHydrated,
        t: { ...copy[locale], ...homeCopy[locale] },
        setLocale,
        toggleTheme,
      }}
    >
      {children}
    </FrontendPreferencesContext.Provider>
  )
}

export const useFrontendPreferences = () => {
  const context = useContext(FrontendPreferencesContext)
  if (!context) {
    throw new Error(
      "useFrontendPreferences must be used within FrontendPreferencesProvider"
    )
  }

  return context
}
